import {
    FetchState,
    IndividualFee,
    InvestmentCustodian,
    InvestmentFeeSchedule,
    PartnerBilling,
    PartnerFeeWaiver,
} from "@/shared/types";
import {
    BankCustodian,
    BulkFileUploadStatus,
    FileUploadRequest,
    PartnerGetResponse,
    PartnerInvestmentThreshold,
    PartnerListItem,
    PartnerSave,
    PartnerSaveResponse,
    PartnerState,
    PartnerUserBulkResponse
} from "../types";
import * as bankService from "@/shared/api/bankService";
import * as billingService from "@/shared/api/billingService";
import * as userService from "@/shared/api/userService";
import * as drivewealthService from "@/shared/api/drivewealthService";
import moment from "moment";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import { DefaultFeeSchedules } from "@/admin/constants";
import { serviceCallNotFoundWithDefault } from "@/shared/errorHelper";
import { downloadCsvFile } from "@/shared/fileDownload";

const pageSize = 20;

export const initialState: PartnerState = {
    fetchState: FetchState.Uninitialized,
    pageNumber: 1,
    partners: [],
    tpaIds: [],
    tpaIdGetFetchState: FetchState.Uninitialized,
    tpaIdPostFetchState: FetchState.Uninitialized,
    partner: null,
    feeWaiverFetchState: FetchState.Uninitialized,
    feeWaiver: null,
    investmentFeePartnerId: 0,
    investmentFeeScheduleState: FetchState.Uninitialized,
    investmentFeeSchedules: null,
    investmentThresholds: [],
    investmentThresholdFetchState: FetchState.Uninitialized,
    achCompanyNameFetchState: FetchState.Uninitialized,
    achCompanyName: null,
    bulkFilesFetchState: FetchState.Uninitialized,
    bulkFiles: null
};

function isBeyondBounds (state: PartnerState, pageIndex: number) {
    return !state.partners ||
        !state.partners.length ||
        state.partners.length <= pageIndex ||
        pageIndex < 0;
}

function pageNumberToIndex (pageNumber: number) {
    return (pageNumber - 1) * pageSize;
}

export const usePartnerStore = defineStore("partner", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        hasPartners: state => state.partners.length > 0,
        isLoaded: state => state.fetchState === FetchState.Loaded,
        investmentThresholdLoaded: state => state.investmentThresholdFetchState === FetchState.Loaded,
        pageIndex: state => pageNumberToIndex(state.pageNumber),
        pageRange () {
            return _.range(1, this.pagesTotal + 1);
        },
        pagesTotal: state => Math.ceil(state.partners.length / pageSize),
        getPartners () {
            const pageIndex = this.pageIndex;
            if (isBeyondBounds(this, pageIndex)) {
                return new Array<PartnerListItem>(0);
            }

            return _.cloneDeep(
                _.slice(this.partners, pageIndex, Math.min(this.partners.length, pageIndex + pageSize)));
        },
        allPartners: state => state.partners,
        partnerWithId: state => (partnerId: number) => state.partners.find(p => p.id === partnerId),
        isFeeWaiverLoaded: state => state.feeWaiverFetchState === FetchState.Loaded,
        isInvestmentFeeSchedulesLoaded: state => state.investmentFeeScheduleState === FetchState.Loaded,
        areTpaIdsLoaded: state => state.tpaIdGetFetchState === FetchState.Loaded,
        partnerInvestmentThreshold: state => (partnerId: number) => state.investmentThresholds.find(
            p => p.partnerId === partnerId),
        achCompanyNameLoaded: state => state.achCompanyNameFetchState === FetchState.Loaded,
        isHsabCustodian: state => partnerId => {
            const partner = state.partners.find(p => p.id === parseInt(partnerId));
            const custodian = partner ? partner.custodian : BankCustodian.None;
            const isHsab = custodian === BankCustodian.HsaBank;
            return isHsab;
        },
        partnerName: state => partnerId => {
            const partner = state.partners.find(p => p.id === parseInt(partnerId));
            const partnername = partner ? partner.name : "";
            return partnername;
        },
        hasDrivewealthAsInvestmentProvider: state => partnerId => {
            const investmentThreshold = state.investmentThresholds.find(
                p => p.partnerId === partnerId);
            const isDrivewealth = investmentThreshold.investmentProvider === InvestmentCustodian.Drivewealth;
            return isDrivewealth;
        },
        getBulkFiles: state => () => {
            return state.bulkFiles;
        }
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setTpaGetFetchState (fetchState: FetchState) {
            this.tpaIdGetFetchState = fetchState;
        },
        setTpaPostFetchState (fetchState: FetchState) {
            this.tpaIdPostFetchState = fetchState;
        },
        setPageNumber (pageNumber: number) {
            if (isBeyondBounds(this, pageNumberToIndex(pageNumber))) {
                this.pageNumber = 0;
                return;
            }

            this.pageNumber = pageNumber;
        },
        setPartner (partner: PartnerSave) {
            this.partner = partner;
        },
        setPartnerTpaId (tpaId: string) {
            this.partner.tpaId = tpaId;
        },
        setPartners (response: PartnerGetResponse) {
            if (!response || !response.partners || !response.partners.length) {
                return;
            }

            this.pageNumber = 1;
            this.partners = _.map(response.partners, partner => ({
                id: partner.id,
                name: partner.name,
                dateCreated: moment(partner.dateCreated).utc(),
                dateSignedUp: partner.dateSignedUp ? moment(partner.dateSignedUp).utc() : null,
                dateTerminated: partner.dateTerminated ? moment(partner.dateTerminated).utc() : null,
                canViewEmployerSsns: partner.canViewEmployerSsns,
                usersAreAutomaticallyRedirectedToWex: partner.usersAreAutomaticallyRedirectedToWex,
                custodian: partner.custodian
            }));
        },
        setTpaIds (tpaIds: string[]) {
            this.tpaIds = tpaIds;
        },
        setFeeWaiverFetchState (fetchState: FetchState) {
            this.feeWaiverFetchState = fetchState;
        },
        setFeeWaiver (feeWaiver: PartnerFeeWaiver) {
            this.feeWaiver = feeWaiver;
        },
        setInvestmentFeeScheduleFetchState (fetchState: FetchState) {
            this.investmentFeeScheduleState = fetchState;
        },
        setInvestmentFeeSchedules (partnerId: number, feeSchedules: InvestmentFeeSchedule[]) {
            this.investmentFeePartnerId = partnerId;
            this.investmentFeeSchedules = feeSchedules;
            this.investmentFeeScheduleState = FetchState.Loaded;
        },
        setInvestmentThresholdFetchState (fetchState: FetchState) {
            this.investmentThresholdFetchState = fetchState;
        },
        setInvestmentThresholds (investmentThreshold: PartnerInvestmentThreshold) {
            this.investmentThresholds = [ ...this.investmentThresholds, investmentThreshold ];
        },
        setAchCompanyName (companyName: string) {
            this.achCompanyName = companyName;
        },
        setAchCompanyNameFetchState (fetchState: FetchState) {
            this.achCompanyNameFetchState = fetchState;
        },
        setBulkFilesFetchState (fetchState: FetchState) {
            this.bulkFilesFetchState = fetchState;
        },
        setBulkFiles (bulkFiles: PartnerUserBulkResponse) {
            this.bulkFiles = bulkFiles;
        },
        partnerLoad (partnerId: number): Promise<PartnerSaveResponse> {
            const billingServicePromise = billingService.getPartnerBilling(partnerId)
                .catch(error => {
                    // handle 404, throw anything else
                    if (error.response && error.response.status === 404) {
                        const nullPartnerBilling: PartnerBilling = {
                            contractOwner: null,
                            invoicing: null
                        };
                        return nullPartnerBilling;
                    }
                    throw error;
                });

            const individualFeePromise = billingService.getIndividualFee(partnerId)
                .catch(error => {
                    // handle 404, throw anything else
                    if (error.response && error.response.status === 404) {
                        const nullIndividualFee: IndividualFee = {
                            monthlyFeeAmount: null,
                            effectiveStartDate: null,
                            feeWaiverType: null,
                            feeWaiverThreshold: null
                        };
                        return nullIndividualFee;
                    }
                    throw error;
                });

            const bankServicePromise = bankService.getPartnerTpaId(partnerId);
            const investmentThresholdPromise = bankService.getPartnerInvestmentThreshold(partnerId);
            const partnerIndividualEmployerIdPromise = bankService.getPartnerIndividualEmployerId(partnerId);
            const drivewealthPromise = drivewealthService.getPartnerInvestmentTypes(partnerId);
            const feeCollectionAccountPromise = billingService.getPartnerFeeCollection(partnerId);
            return userService.adminPartner(partnerId)
                .then(userServiceResponse => ({ userServiceData: userServiceResponse.data.partner }))
                .then(prior => bankServicePromise.then(bankServiceResponse =>
                    ({ ...prior, bankServiceData: bankServiceResponse.data })))
                .then(prior => billingServicePromise.then(billingServiceResponse =>
                    ({ ...prior, billingServiceData: billingServiceResponse })))
                .then(prior => individualFeePromise.then(individualFeeResponse =>
                    ({ ...prior, individualFeeData: individualFeeResponse })))
                .then(prior => investmentThresholdPromise.then(investmentThresholdResponse =>
                    ({ ...prior, investmentThresholdData: investmentThresholdResponse.data })))
                .then(prior => partnerIndividualEmployerIdPromise.then(partnerIndividualEmployerIdResponse =>
                    ({ ...prior, partnerIndividualEmployerId: partnerIndividualEmployerIdResponse.data })))
                .then(prior => drivewealthPromise.then(partnerInvestmentTypeResponse =>
                    ({ ...prior, partnerInvestmentType: partnerInvestmentTypeResponse.data })))
                .then(prior => feeCollectionAccountPromise.then(feeCollectionResponse =>
                    ({ ...prior, feeCollection: feeCollectionResponse.data })))
                .then(compositeData => {
                    const userSlice = compositeData.userServiceData;
                    const tpaId = compositeData.bankServiceData;
                    const billingSlice = compositeData.billingServiceData;
                    const individualFeeSlice = compositeData.individualFeeData;
                    const investmentSlice = compositeData.investmentThresholdData;
                    const partnerIndividualEmployerId = compositeData.partnerIndividualEmployerId;
                    const feeCollectionSlice = compositeData.feeCollection;
                    const partnerSave: PartnerSave = {
                        partnerId,
                        partnerName: userSlice.partnerName,
                        partnerWebsite: userSlice.partnerWebsite,
                        partnerEin: userSlice.partnerEin,
                        partnerPhone: userSlice.partnerPhone,
                        partnerAddress1: userSlice.partnerAddress1,
                        partnerAddress2: userSlice.partnerAddress2,
                        partnerCity: userSlice.partnerCity,
                        partnerState: userSlice.partnerState,
                        partnerZip: userSlice.partnerZip,
                        partnerCustodian: userSlice.partnerCustodian,
                        partnerCustodianLocked: userSlice.partnerCustodianLocked,
                        ownerFirstName: userSlice.ownerFirstName,
                        ownerLastName: userSlice.ownerLastName,
                        ownerPhone: userSlice.ownerPhone,
                        ownerJobTitle: userSlice.ownerJobTitle,
                        ownerEmail: userSlice.ownerEmail,
                        partnerDisplayName: userSlice.partnerDisplayName,
                        externalPartnerId: userSlice.externalPartnerId,
                        tpaId,
                        functionTypes: userSlice.functionTypes,
                        invoicing: billingSlice.invoicing,
                        contractOwner: billingSlice.contractOwner,
                        individualMonthlyFeeAmount: individualFeeSlice.monthlyFeeAmount,
                        individualFeeStartDate: individualFeeSlice.effectiveStartDate,
                        servicingPartners: userSlice.servicingPartners,
                        automaticallyRemoveOrphans: userSlice.automaticallyRemoveOrphans,
                        investmentThreshold: investmentSlice,
                        individualEmployerId: partnerIndividualEmployerId,
                        feeWaiverType: individualFeeSlice.feeWaiverType,
                        feeWaiverThreshold: individualFeeSlice.feeWaiverThreshold,
                        investmentEnrollmentType: compositeData.partnerInvestmentType,
                        ssoDisabled: userSlice.ssoDisabled,
                        feeCollectionType: feeCollectionSlice.feeCollectionAccountType,
                        ssoUserSyncStatus: userSlice.ssoUserSyncStatus
                    };
                    this.setPartner(partnerSave);
                    return {
                        error: "",
                        partner: partnerSave
                    };
                });
        },
        partnerSave (partnerSave: PartnerSave): Promise<void> {
            const billingData = { invoicing: partnerSave.invoicing, contractOwner: partnerSave.contractOwner };
            const individualFeeData: IndividualFee = {
                monthlyFeeAmount: partnerSave.individualMonthlyFeeAmount,
                effectiveStartDate: partnerSave.individualFeeStartDate,
                feeWaiverType: partnerSave.feeWaiverType,
                feeWaiverThreshold: partnerSave.feeWaiverThreshold
            };
            return userService.adminSavePartner(partnerSave).then(response => {
                    return response.data.partner.partnerId;
                })
                .then(partnerId => {
                    if (!partnerSave.tpaId) {
                        return partnerId;
                    }

                    return bankService.savePartnerTpaId({ partnerId, tpaId: partnerSave.tpaId })
                        .then(() => partnerId);
                })
                .then(partnerId => {
                    if (!partnerSave.individualEmployerId) {
                        return partnerId;
                    }

                    return bankService.savePartnerIndividualEmployerId(
                            { partnerId, individualEmployerId: partnerSave.individualEmployerId })
                        .then(() => partnerId);
                })
                .then(partnerId => {
                    return billingService.putPartnerBilling(partnerId, billingData)
                        .then(() => partnerId);
                })
                .then(partnerId => {
                    if (!individualFeeData.monthlyFeeAmount && !individualFeeData.feeWaiverThreshold) {
                        return partnerId;
                    }
                    return new Promise((resolve, reject) => {
                        let action: () => void;
                        let attempts = 10;
                        action = () => {
                            billingService.putIndividualFee(partnerId, individualFeeData)
                                .then(() => resolve(partnerId))
                                .catch(err => {
                                    attempts--;
                                    // for new partner, retry if partner is not already added
                                    if (attempts > 0 && err && err.response && err.response.status === 404) {
                                        window.setTimeout(action, 5000);
                                        return partnerId;
                                    }
                                    reject(err);
                                });
                        };
                        action();
                    }).then(() => partnerId);
                })
                .then(partnerId => bankService.setPartnerInvestmentThreshold(partnerId, partnerSave.investmentThreshold)
                    .then(() => partnerId))
                .then(partnerId => {
                    if (!partnerSave.investmentEnrollmentType) {
                        return partnerId;
                    }
                    return drivewealthService.setPartnerInvestmentTypes(partnerId, partnerSave.investmentEnrollmentType)
                        .then(() => partnerId);
                })
                .then(partnerId => {
                    billingService.putPartnerFeeCollection(partnerId,
                        { feeCollectionAccountType: partnerSave.feeCollectionType });
                })
                .then(() => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Partner saved successfully.");
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getTpaIds (): Promise<any> {
            return new Promise<void>((resolve, reject) => {
                if (this.tpaIdGetFetchState === FetchState.Loaded) {
                    return resolve(this.tpaIds);
                }

                this.setTpaGetFetchState(FetchState.Loading);
                bankService.getTpaIds()
                    .then(response => {
                        if (!response) {
                            reject({ message: "There was a problem reaching our servers", payload: response });
                            return;
                        }

                        this.setTpaIds(response.data.tpaIds);
                        resolve();
                    })
                    .catch(err => reject(err))
                    .finally(() => {
                        this.setTpaGetFetchState(FetchState.Loaded);
                    });
            });
        },
        saveTpaId (data: { tpaId: string, partnerId: number }): Promise<any> {
            this.setTpaPostFetchState(FetchState.Loading);

            if (!data.tpaId) {
                return;
            }
            return new Promise<void>((resolve, reject) => {
                let action: () => void;
                let attempts = 10;
                action = () => {
                    bankService.savePartnerTpaId(data)
                        .then(_ => {
                            this.setPartnerTpaId(data.tpaId);
                            return resolve();
                        })
                        .catch(err => {
                            attempts--;
                            if (attempts > 0 && err && err.response && err.response.status === 400) {
                                // save did not occur yet, keep trying
                                window.setTimeout(action, 5000);
                                return;
                            }

                            reject(err);
                        });
                };

                action();
            })
                .then(response => this.setTpaPostFetchState(FetchState.Loaded))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        reload (force = true) {
            if (this.fetchState === FetchState.Loading || (this.fetchState === FetchState.Loaded && !force)) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);

            return userService.adminPartners().then(response => {
                this.setPartners(response.data);
                this.setFetchState(FetchState.Loaded);
            }).catch(err => {
                this.setFetchState(FetchState.Uninitialized);
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        validateChosenPartner (data: { currentPartnerId: number, chosenPartnerId: number }) {
            return bankService.validateEmployerPartnerChange(data.currentPartnerId, data.chosenPartnerId)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadFeeWaiverSettings (partnerId: number) {
            if (this.feeWaiverFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            return billingService.getPartnerFeeWaiver(partnerId)
                .then(response => {
                    this.setFeeWaiver(response.data);
                    this.setFeeWaiverFetchState(FetchState.Loaded);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadPartnerInvestmentThresholds (partnerId: number) {
            if (this.investmentThresholdFetchState === FetchState.Loading ||
                !!this.partnerInvestmentThreshold(partnerId)) {
                return Promise.resolve();
            }

            this.setInvestmentThresholdFetchState(FetchState.Loading);

            return bankService.getPartnerInvestmentThreshold(partnerId)
                .then(response => {
                    this.setInvestmentThresholds({ ...response.data, partnerId });
                    this.setInvestmentThresholdFetchState(FetchState.Loaded);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadInvestmentFeeSchedules (partnerId: number) {
            if (this.investmentFeeScheduleState !== FetchState.Uninitialized
                && this.investmentFeePartnerId === partnerId) {
                return Promise.resolve();
            }
            this.setInvestmentFeeScheduleFetchState(FetchState.Loading);
            this.investmentFeePartnerId = partnerId;

            return serviceCallNotFoundWithDefault(
                billingService.getPartnerInvestmentFeeSchedules(partnerId),
                () => _.cloneDeep(DefaultFeeSchedules))
                .then(data => {
                    if (this.investmentFeePartnerId === partnerId) {
                        this.setInvestmentFeeSchedules(partnerId, data);
                    }
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        saveInvestmentFeeSchedules ({ partnerId, feeSchedules }:
                                        { partnerId: number, feeSchedules: InvestmentFeeSchedule[] }) {
            const notifierStore = useNotifierStore();
            return billingService.updatePartnerInvestmentFeeSchedules(partnerId, feeSchedules)
                .then(() => this.setInvestmentFeeSchedules(partnerId, feeSchedules))
                .then(() => notifierStore.setSuccess(
                    `Investment Fee Collection Schedules saved for ${this.partner.partnerName}.`))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadAchCompanyName (partnerId: number) {
            if (this.achCompanyNameFetchState === FetchState.Loading) {
                return Promise.resolve();
            }
            this.setAchCompanyNameFetchState(FetchState.Loading);
            return bankService.getAchCompanyName(partnerId)
                .then(response => {
                    this.setAchCompanyName(response.data.companyName);
                    this.setAchCompanyNameFetchState(FetchState.Loaded);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        saveAchCompanyName (params: { partnerId: number, companyName: string }) {
            const notifierStore = useNotifierStore();
            return bankService.putAchCompanyName(params.partnerId, { companyName: params.companyName })
                .then(() => this.setAchCompanyName(params.companyName))
                .then(() => notifierStore.setSuccess(`ACH branding saved`))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        uploadBulkAddFile (request: FileUploadRequest): Promise<any> {
            return new Promise<any>((resolve, reject) => {
                userService.postPartnerBulkAddFile(request.file)
                    .then(response => {
                        const notifierStore = useNotifierStore();
                        notifierStore.setInfo("Your file is processing, check the file status below.");

                        resolve(response);
                    })
                    .catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                        reject(err);
                    });
            });
        },
        uploadBulkDeleteFile (request: FileUploadRequest): Promise<any> {
            return new Promise<any>((resolve, reject) => {
                userService.postPartnerBulkDeleteFile(request.file)
                    .then(response => {
                        const notifierStore = useNotifierStore();
                        notifierStore.setInfo("Your file is processing, check the file status below.");

                        resolve(response);
                    })
                    .catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                        reject(err);
                    });
            });
        },
        getAllBulkFiles (pageNumber: number, pageSize: number, fileType: number): Promise<any> {
            return new Promise<any>((resolve, reject) => {
                userService.getBulkFiles(pageNumber, pageSize, fileType)
                    .then(response => {
                        this.setBulkFiles(response.data);
                        resolve(response.data);
                    })
                    .catch(err => {
                        const notifierStore = useNotifierStore();
                        notifierStore.setInfo("Error" + err);
                        storeHelper.handleError({ error: err, rethrow: true });
                        reject(err);
                    });
            });
        },
        searchBulkFiles (pageNumber: number, pageSize: number, fileType: number, fileName: string, status: BulkFileUploadStatus, startDate: string, endDate: string): Promise<any> {
            return new Promise<any>((resolve, reject) => {
                this.setBulkFilesFetchState(FetchState.Loading);
                userService.searchBulkFiles(pageNumber, pageSize, fileType, fileName, status, startDate, endDate)
                    .then(response => {
                        this.setBulkFiles(response.data);
                        this.setBulkFilesFetchState(FetchState.Loaded);
                        resolve(response.data);
                    })
                    .catch(err => {
                        const notifierStore = useNotifierStore();
                        notifierStore.setInfo("Error" + err);
                        storeHelper.handleError({ error: err, rethrow: true });
                        this.setBulkFilesFetchState(FetchState.Failed);
                        reject(err);
                    });
            });
        },
        downloadBulkFileResult (partnerUserFileId: number): Promise<void> {
            const notifierStore = useNotifierStore();
            return userService.downloadPartnerUserBulkResultFile(partnerUserFileId).then(response => {
                const fileName = `partneruser-${partnerUserFileId}-bulk-file-result.csv`;
                downloadCsvFile(response.data, fileName);
            }).catch(() => {
                notifierStore.setDanger("Could not download the bulk upload results at this time. Please try again later.");
            });
        }
    }
});
